/**
 * PATH: packages/ui/src/components/staleBundle.ts
 * FILE_ID: UI-RECOVERY-STALE-BUNDLE-001
 * PURPOSE: Shared stale-deploy detection and one-shot reload guard.
 * ROLE IN ARCHITECTURE: Pure recovery helpers for AppErrorBoundary,
 *          KanbanErrorBoundary, and classifyKanbanError.
 * MAIN EXPORTS: looksLikeStaleBundle, shouldAttemptReload,
 *               claimStaleBundleReload, RELOAD_GUARD_KEY
 * NON-RESPONSIBILITIES: Does not reload the page or render UI.
 * NOTES FOR FUTURE AI: Classify stale-chunk errors here BEFORE any
 *          "failed to fetch" network heuristic. Kanban used to treat
 *          dynamic-import 404s as a dropped connection.
 */

export const RELOAD_GUARD_KEY = 'AppErrorBoundary:lastReload'
export const RELOAD_GUARD_WINDOW_MS = 60_000

/**
 * True when the error is a missing hashed chunk or a half-applied
 * cached bundle, not a live application bug.
 */
export function looksLikeStaleBundle(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false

  const name = (error as { name?: unknown }).name
  const message = (error as { message?: unknown }).message
  const nameStr = typeof name === 'string' ? name : ''
  const msg = typeof message === 'string' ? message : ''

  if (nameStr === 'ChunkLoadError') return true

  if (/failed to fetch dynamically imported module/i.test(msg)) return true
  if (/error loading dynamically imported module/i.test(msg)) return true
  if (/loading chunk \d+ failed/i.test(msg)) return true
  if (/loading css chunk \d+ failed/i.test(msg)) return true

  if (nameStr === 'ReferenceError' && /^[A-Za-z_$][\w$]* is not defined$/.test(msg)) {
    return true
  }

  return false
}

/**
 * False when we already auto-reloaded inside RELOAD_GUARD_WINDOW_MS.
 */
export function shouldAttemptReload(
  now: number,
  getItem: (key: string) => string | null,
): boolean {
  const last = getItem(RELOAD_GUARD_KEY)
  if (last === null || last === undefined || last === '') return true
  const lastMs = Number.parseInt(last, 10)
  if (Number.isNaN(lastMs)) return true
  return now - lastMs > RELOAD_GUARD_WINDOW_MS
}

/**
 * Reserve the one-shot stale reload. Callers then schedule
 * window.location.reload(). Shared so Kanban and the shell cannot
 * each fire a loop.
 */
export function claimStaleBundleReload(
  now: number,
  getItem: (key: string) => string | null,
  setItem: (key: string, value: string) => void,
): boolean {
  if (!shouldAttemptReload(now, getItem)) return false
  try {
    setItem(RELOAD_GUARD_KEY, String(now))
  } catch {
    // sessionStorage can throw in private-browsing / disabled-storage modes
  }
  return true
}
